/**
 * <load-more-products> — appends the next paginated page in place of a page
 * reload, with an optional infinite-scroll trigger and a progress indicator.
 *
 * Re-instantiated automatically when Dawn's FacetFiltersForm re-renders
 * #ProductGridContainer (innerHTML reset upgrades the new custom element).
 */
class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();
    this.total = parseInt(this.dataset.total, 10) || 0;
    this.loading = false;
    this.grid = document.getElementById('product-grid');
    this.btn = this.querySelector('[data-load-more]');
    this.countEl = this.querySelector('[data-shown-count]');
    this.progressEl = this.querySelector('[data-progress]');
  }

  connectedCallback() {
    if (this.btn) {
      this.btn.addEventListener('click', this.onClick);
      // Infinite scroll, paced one page per scroll: auto-load once when the
      // button nears the viewport, then disarm until it leaves view again so
      // a single page-load never cascades into many.
      this.autoArmed = true;
      this.observer = new IntersectionObserver(
        (entries) => {
          const inView = entries[0].isIntersecting;
          if (inView && this.autoArmed && !this.loading) {
            this.autoArmed = false;
            this.loadNext();
          } else if (!inView) {
            this.autoArmed = true;
          }
        },
        { rootMargin: '300px 0px' }
      );
      this.observer.observe(this.btn);
    }
    this.updateProgress();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.btn) this.btn.removeEventListener('click', this.onClick);
  }

  onClick = (e) => {
    e.preventDefault();
    this.loadNext();
  };

  updateProgress() {
    if (!this.grid) return;
    const shown = this.grid.querySelectorAll('.grid__item').length;
    if (this.countEl) this.countEl.textContent = shown;
    if (this.progressEl && this.total > 0) {
      this.progressEl.style.width = Math.min(100, (shown / this.total) * 100) + '%';
    }
  }

  async loadNext() {
    const url = this.btn && this.btn.getAttribute('href');
    if (!url || this.loading || !this.grid) return;
    this.loading = true;
    this.classList.add('is-loading');
    this.btn.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      // Append the next page's product cards.
      const incoming = doc.querySelectorAll('#product-grid > .grid__item');
      const frag = document.createDocumentFragment();
      incoming.forEach((li) => frag.appendChild(li));
      this.grid.appendChild(frag);

      // Chain to the page after this one, or retire the button when done.
      const nextBtn = doc.querySelector('[data-load-more]');
      if (nextBtn) {
        this.btn.setAttribute('href', nextBtn.getAttribute('href'));
        this.btn.removeAttribute('aria-busy');
      } else {
        if (this.observer) this.observer.disconnect();
        this.btn.remove();
        this.btn = null;
      }
      this.updateProgress();
    } catch (e) {
      if (this.btn) this.btn.removeAttribute('aria-busy');
    } finally {
      this.loading = false;
      this.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('load-more-products')) {
  customElements.define('load-more-products', LoadMoreProducts);
}
